import React, { useMemo, useState, useEffect } from 'react';

import { List } from 'antd';

import styled from 'styled-components';

import { readUserLogs } from '@libs/NwLogs';

import { getCustomers } from '@hooks/API/Contact/UseContactsList';

import ContactAlertPopover from '@components/ContactAlert/ContactAlertPopover';
import FlexContainer from '@components/Gui/FlexContainer';
import { NwLinkButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwIcon from '@components/Gui/NwIcon';
import ProjectTitle from '@components/Project/ProjectTitle';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Container = styled.div`
    display: flex;
    flex-direction: column;

    .projects-list {
        flex: ${(props) => (props.searchOnly ? '100%' : '60%')};
        padding: 0 1em 1em;

        .ant-list-empty-text {
            padding: 0;

            .ant-empty {
                margin: 0;
            }
        }

        .ant-list-item {
            .project-list-item {
                cursor: pointer;
                width: 100%;
                display: flex;

                .project-list-icon {
                    font-size: 2rem;
                    margin: 0 1rem;
                    line-height: 1em;
                }
                .project-list-details {
                    flex: 1 1 0;
                    padding-right: 1rem;
                    .project-clientname {
                        text-transform: uppercase;
                        font-size: 0.85rem;
                        line-height: 1em;
                    }
                    .project-name {
                        font-weight: bold;
                    }
                }
                .project-list-description {
                    width: 40%;
                    .project-description {
                        font-size: 0.75rem;
                        color: ${(props) => props.theme.TextColorMuted};
                        font-style: italic;
                    }
                    .project-period {
                        text-transform: uppercase;
                        font-size: 0.75rem;
                    }
                }
            }
        }
    }

    .clients-list {
        flex: ${(props) => (props.searchOnly ? '0%' : '40%')};
        padding: 0 1em 1em;
        display: ${(props) => props.searchOnly && 'none'};

        .ant-list-item {
            cursor: pointer;

            .client-container {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                width: 100%;
                flex-direction: row;
                gap: 1rem;
                padding-left: 1rem;

                .clients-list-icon {
                    font-size: 2em;
                }
                .client-name-container {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;

                    .client-name {
                        font-weight: bold;
                        text-transform: uppercase;
                    }
                }
            }
        }
    }

    .new-client {
        flex: ${(props) => (props.searchOnly ? '0%' : '40%')};
        padding: 0 1em 1em;
        display: ${(props) => props.searchOnly && 'none'};

        .ant-list-item {
            cursor: pointer;
            .new-client-icon {
                font-size: 2em;
                margin: 0 1em;
            }
            .new-client-text {
                font-weight: bold;
            }
        }
    }
`;

const ProjectsCustomers = ({
    projects,
    term,
    searchOnly,
    searchMode,
    onSelectProject,
    onSelectCustomer,
    onAddCustomer,
}) => {
    const customers = getCustomers();

    const userLogs = readUserLogs();
    const [defaultProjects, setDefaultProjects] = useState([]);
    const [showAllProjects, setShowAllProjects] = useState(false);
    const [showAllCustomers, setShowAllCustomers] = useState(false);

    const clientsAndAgencies = useMemo(() => {
        return customers.filter((customer) => customer.ST === 1 || customer.ST === 2);
    }, [customers]);

    useEffect(() => {
        if (showAllProjects) {
            setShowAllProjects(false);
        }
        if (showAllCustomers) {
            setShowAllCustomers(false);
        }

    }, [term]);

    useEffect(() => {
        if (searchMode) {
            const projects = userLogs
                .filter((log) => log.t === 'project')
                .reverse()
                .map((project) => {
                    const names = project.name.split('|');
                    return {
                        ID: project.id,
                        Name: names[0],
                        Contact: { Name: names[1] },
                        Product: names.length > 2 ? names[2] : '',
                        ProjectCategoryID: names.length > 3 ? Number(names[3]) : null,
                    };
                });
            setDefaultProjects(projects);
        }

    }, []);

    useEffect(() => {
        if (defaultProjects.length > 0) {
            setDefaultProjects([]);
        }

    }, [term]);

    const filteredCustomers = useMemo(() => {
        return term.length >= 3
            ? clientsAndAgencies.filter((customer) => customer.N.toLowerCase().includes(term.toLowerCase()))
            : [];

    }, [clientsAndAgencies, term]);

    const getTextLine = (line) => {
        switch (line) {
            case 'selectproject':
                return 'select an existing project';
            case 'createproject':
                return 'create a new project for';
            default:
        }
    };

    const ProjectList = () => {
        if (defaultProjects.length > 0 || projects.length > 0) {
            return (
                <div className="projects-list">
                    <NwDivider textSize="big" bold dark>
                        {getTextLine('selectproject')}
                    </NwDivider>
                    <List
                        itemLayout="horizontal"
                        dataSource={
                            defaultProjects.length
                                ? showAllProjects
                                    ? defaultProjects
                                    : [...defaultProjects].splice(0, 4)
                                : showAllProjects
                                  ? projects
                                  : [...projects].splice(0, 4)
                        }
                        renderItem={(project) => {
                            return (
                                <List.Item className="ant-list-item-top" onClick={() => onSelectProject(project.ID)}>
                                    <ProjectTitle small project={project} pointer noProjectText />
                                </List.Item>
                            );
                        }}
                    />
                    {!showAllProjects && (defaultProjects.length > 4 || projects.length > 4) && (
                        <FlexContainer justifyContent="center">
                            <NwLinkButton onClick={() => setShowAllProjects(true)} label="Load More" />
                        </FlexContainer>
                    )}
                </div>
            );
        } else {
            return <></>;
        }
    };

    const ClientsList = () => {
        if (filteredCustomers.length > 0) {
            return (
                <div className="clients-list">
                    <NwDivider textSize="big" bold dark>
                        {getTextLine('createproject')}
                    </NwDivider>
                    <List
                        itemLayout="horizontal"
                        //dataSource={defaultCustomers.length > 0 ? defaultCustomers : filteredCustomers}
                        dataSource={showAllCustomers ? filteredCustomers : [...filteredCustomers].splice(0, 4)}
                        renderItem={(customer) => {
                            return (
                                <List.Item
                                    className="ant-list-item-top"
                                    onClick={() => {
                                        onSelectCustomer(customer);
                                    }}
                                >
                                    <div className="client-container">
                                        <div className="clients-list-icon">
                                            <NwIcon icon={byPrefixAndName.fasl['address-card']} />
                                        </div>
                                        <div className="client-name-container">
                                            {customer.AL && (
                                                <div className="alert-icon">
                                                    <ContactAlertPopover contact={customer} big />
                                                </div>
                                            )}
                                            <div className="client-name">{customer.N}</div>
                                        </div>
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                    {!showAllCustomers && filteredCustomers.length > 4 && (
                        <FlexContainer justifyContent="center">
                            <NwLinkButton onClick={() => setShowAllCustomers(true)} label="Load More" />
                        </FlexContainer>
                    )}
                </div>
            );
        } else {
            return <></>;
        }
    };

    const NewClient = () => {
        if (term) {
            return (
                <div className="new-client">
                    <NwDivider textSize="big" bold dark>
                        or
                    </NwDivider>
                    <List itemLayout="horizontal">
                        <List.Item className="ant-list-item-top" onClick={() => onAddCustomer(term)}>
                            <FlexContainer>
                                <div className="new-client-icon">
                                    <NwIcon icon={byPrefixAndName.fass['plus-circle']} />
                                </div>
                                <FlexContainer column alignItems="flex-start">
                                    <div className="new-client-text">create a new "{term}" client</div>
                                </FlexContainer>
                            </FlexContainer>
                        </List.Item>
                    </List>
                </div>
            );
        } else {
            return <></>;
        }
    };

    return (
        <Container searchOnly={searchOnly}>
            {searchMode ? (
                <>
                    <ClientsList />
                    <ProjectList />
                    <NewClient />
                </>
            ) : (
                <>
                    <ClientsList />
                    <NewClient />
                    <ProjectList />
                </>
            )}
        </Container>
    );
};

export default ProjectsCustomers;
